import { ethers } from "ethers";
import { convertBigNumber } from "src/ether-utils/utils";
import { Multicall } from "ethereum-multicall";
import LendingPoolAbi from "src/assets/abis/LendingPool.json";
import Bep20Abi from "src/assets/abis/Bep20Abi.json";
import StakedIncentivesABI from "src/assets/abis/StakedIncentivesABI.json";
import AaveOracle from "src/assets/abis/AaveOracle.json";
import TToken from "src/assets/abis/tokens/TToken.json";
import DebtToken from "src/assets/abis/tokens/DebtToken.json";

export default class TravaFetcher {
  constructor(poolAddress, incentiveAddress, oracleAddress, provider, network) {
    this._poolAddress = poolAddress;
    this._incentiveAddress = incentiveAddress;
    this._oracleAddress = oracleAddress;

    this.provider = provider;
    this.network = network;

    this._multiCall = new Multicall({ ethersProvider: this.provider, tryAggregate: true });
    this._lendingPoolContract = new ethers.Contract(poolAddress, LendingPoolAbi, provider);

    this.isFetched = false;
    this._data = null;
    this._addresses = [];

    this._getAllReservesAddress = this._getAllReservesAddress.bind(this);
    this.getOraclePrice = this.getOraclePrice.bind(this);
    this._getTokenData = this._getTokenData.bind(this);
    this._decodeTokenData = this._decodeTokenData.bind(this);
    this._getIncentiveData = this._getIncentiveData.bind(this);
    this._getAssetTotalData = this._getAssetTotalData.bind(this);
    this._mergeFinalResult = this._mergeFinalResult.bind(this);
    this.getTokenData = this.getTokenData.bind(this);
  }

  async _getAllReservesAddress() {
    const reservesAddress = await this._lendingPoolContract.getReservesList();
    return reservesAddress;
  }

  async getOraclePrice(addresses = null) {
    let addressList = addresses;
    if (!addressList) addressList = this._addresses;
    let _dataCall = [];
    for (let address of addressList) {
      _dataCall.push({
        reference: address,
        contractAddress: this._oracleAddress,
        abi: AaveOracle,
        calls: [{ reference: "getAssetPrice", methodName: "getAssetPrice", methodParameters: [address] }],
      });
    }
    let _result = await this._multiCall.call(_dataCall);
    _result = _result.results;
    let result = {};
    for (let address of addressList) {
      const _data = _result[address].callsReturnContext[0];
      result[address.toLowerCase()] = {
        price: convertBigNumber(_data.returnValues[0], 8, 2),
      };
    }
    return result;
  }

  async _getTokenData(reserveAddressList) {
    let _dataCall = [];
    reserveAddressList.forEach((address) => {
      _dataCall.push({
        reference: `bep20_${address}`,
        contractAddress: address,
        abi: Bep20Abi,
        calls: [
          { reference: "decimal", methodName: "decimals" },
          { reference: "symbol", methodName: "symbol" },
        ],
      });
      _dataCall.push({
        reference: `data_${address}`,
        contractAddress: this._poolAddress,
        abi: LendingPoolAbi,
        calls: [{ reference: "getReserveData", methodName: "getReserveData", methodParameters: [address] }],
      });
    });
    const result = await this._multiCall.call(_dataCall);
    return result;
  }

  _decodeTokenData(reserveAddressList, tokenData) {
    const result = {};
    for (var address of reserveAddressList) {
      const bep20Data = tokenData[`bep20_${address}`].callsReturnContext;
      const reserveData = tokenData[`data_${address}`].callsReturnContext;
      result[address.toLowerCase()] = {
        address: address,
        decimal: bep20Data[0].returnValues[0],
        symbol: bep20Data[1].returnValues[0],
        depositRate: convertBigNumber(reserveData[0].returnValues[3], 27, 10),
        borrowRate: convertBigNumber(reserveData[0].returnValues[4], 27, 10),
        tTokenAddress: reserveData[0].returnValues[6],
        debtTokenAddress: reserveData[0].returnValues[7],
      };
    }
    return result;
  }

  async _getIncentiveData(reserveAddressList, tokenData) {
    let _dataCall = [];
    reserveAddressList.forEach((address) => {
      const lowerAddress = address.toLowerCase();
      const _tokenData = tokenData[lowerAddress];
      _dataCall.push({
        reference: address,
        contractAddress: this._incentiveAddress,
        abi: StakedIncentivesABI,
        calls: [
          { reference: "t_getAssetData", methodName: "getAssetData", methodParameters: [_tokenData.tTokenAddress] },
          { reference: "d_getAssetData", methodName: "getAssetData", methodParameters: [_tokenData.debtTokenAddress] },
        ],
      });
    });
    let _result = await this._multiCall.call(_dataCall);
    _result = _result.results;
    let result = {};
    for (var address of reserveAddressList) {
      const _data = _result[address].callsReturnContext;
      result[address.toLowerCase()] = {
        tTokenEPS: convertBigNumber(_data[0].returnValues[1], 18, 5),
        dTokenEPS: convertBigNumber(_data[1].returnValues[1], 18, 5),
      };
    }
    return result;
  }

  async _getAssetTotalData(reserveAddressList, tokenData) {
    const oracleData = await this.getOraclePrice(reserveAddressList);
    let _dataCall = [];
    reserveAddressList.forEach((address) => {
      const lowerAddress = address.toLowerCase();
      const _tokenData = tokenData[lowerAddress];
      _dataCall.push({
        reference: `t_token_${address}`,
        contractAddress: _tokenData.tTokenAddress,
        abi: TToken,
        calls: [{ reference: "t_totalSupply", methodName: "totalSupply" }],
      });
      _dataCall.push({
        reference: `d_token_${address}`,
        contractAddress: _tokenData.debtTokenAddress,
        abi: DebtToken,
        calls: [{ reference: "d_totalSupply", methodName: "totalSupply" }],
      });
    });
    const _result = (await this._multiCall.call(_dataCall)).results;
    let result = {};
    reserveAddressList.forEach((address) => {
      const lowerAddress = address.toLowerCase();
      const _tokenData = tokenData[lowerAddress];
      const _price = oracleData[lowerAddress].price;
      const _tData = _result[`t_token_${address}`].callsReturnContext[0].returnValues[0];
      const _dData = _result[`d_token_${address}`].callsReturnContext[0].returnValues[0];
      const totalSupply = convertBigNumber(_tData, _tokenData.decimal, 2) * _price;
      const totalBorrow = convertBigNumber(_dData, _tokenData.decimal, 2) * _price;
      result[lowerAddress] = { totalSupply, totalBorrow };
    });
    return result;
  }

  _mergeFinalResult(reserveAddressList, tokenData, incentiveData, totalData) {
    let result = {};
    for (let address of reserveAddressList) {
      const lowerAddress = address.toLowerCase();
      result[lowerAddress] = { ...tokenData[lowerAddress], ...incentiveData[lowerAddress], ...totalData[lowerAddress] };
    }
    return result;
  }

  async getTokenData() {
    if (!this.isFetched) {
      this._addresses = await this._getAllReservesAddress();
      let _tokenData = await this._getTokenData(this._addresses);
      _tokenData = this._decodeTokenData(this._addresses, _tokenData.results);
      const _incentiveData = await this._getIncentiveData(this._addresses, _tokenData);
      const _totalData = await this._getAssetTotalData(this._addresses, _tokenData);
      this._data = this._mergeFinalResult(this._addresses, _tokenData, _incentiveData, _totalData);
      this.isFetched = true;
    }
    return this._data;
  }
}
